// src/data/caseFileReview.js
//
// RULING 309 (Bob 2026-10-12): "the general chairs warroom, create an area and a box to drop old case files into for review,
// analytics, assessments, a thorough critical assessment, but balanced fairly and even handedly - just honest but real too".
//
// The War Room's case-file box. A General Chairperson (or, during the build, the administrator) drops an old case file -
// a PDF, scanned or not, of any length - into warRoomCaseFiles/{committee}/... in Storage and a record in
// warRoomCaseFiles/{id}. The server (functions assessCaseFileOnQueue) reads it in sections, extracts each section
// faithfully, then writes one assessment from the extracts. This module holds what both sides need: the limits, the
// section plan, the record's shape, and the two instructions the model is given.
//
// PURE. No Firestore, no network, no clock. Shared with the server (scripts\sync-shared-modules.js).

export const MAX_FILE_BYTES = 100 * 1024 * 1024;   // a scanned 116-page case file is ~44 MB; Storage's rule caps this
export const SECTION_PAGES = 40;                    // the model reads at most 100 PDF pages per request; 40 keeps a scan under ~20 MB
export const STATUSES = Object.freeze(['queued', 'reading', 'assessed', 'failed']);
export const TITLE_MAX = 200;

// The record a drop creates. The server alone writes status beyond 'queued', and the extracts and assessments.
export function newCaseFileRecord({ committee, title, fileName, storagePath, sizeBytes, user, serverTime }) {
  const errors = [];
  const t = String(title || fileName || '').trim().slice(0, TITLE_MAX);
  if (!committee) errors.push('No committee: the box belongs to a committee\'s War Room.');
  if (!fileName || !/\.pdf$/i.test(String(fileName))) errors.push('A case file is dropped as a PDF.');
  if (!(sizeBytes > 0)) errors.push('The file is empty.');
  if (sizeBytes > MAX_FILE_BYTES) errors.push('The file is larger than ' + Math.round(MAX_FILE_BYTES / 1048576) + ' MB.');
  if (!user || !user.uid) errors.push('Not signed in.');
  if (errors.length) return { ok: false, errors, doc: null };
  return {
    ok: true, errors: [],
    doc: {
      committee: String(committee), title: t, fileName: String(fileName).slice(0, 300), storagePath: String(storagePath),
      sizeBytes: Number(sizeBytes),
      uploadedBy: user.uid, uploadedByName: String(user.name || user.email || '').slice(0, 200), uploadedAt: serverTime,
      status: 'queued', requestedBy: user.uid, requestedAt: serverTime,
    },
  };
}

// Page ranges of at most SECTION_PAGES, 1-based and inclusive: [[1,40],[41,80],[81,116]].
export function sectionPlan(pageCount, size = SECTION_PAGES) {
  const n = Math.max(0, Math.floor(Number(pageCount) || 0));
  const out = [];
  for (let a = 1; a <= n; a += size) out.push([a, Math.min(n, a + size - 1)]);
  return out;
}

// What the model is told for each section. It extracts; it does not judge yet.
export function extractInstruction(sectionNo, sectionCount, from, to) {
  return [
    'This is section ' + sectionNo + ' of ' + sectionCount + ' of a railroad labor case file (pages ' + from + '-' + to + ' of the whole file).',
    'Extract, faithfully and completely, what these pages show. Do not judge yet and do not invent anything.',
    'For every item give the page number of the whole file in brackets, e.g. [p. 12].',
    'Extract:',
    '1. Every document in these pages: what it is, its date, who wrote it to whom (by role and name as written).',
    '2. Every dated act, in order: incident, notices, requests, meetings, hearing, decision, appeals, conferences, referral.',
    '3. The charges or the claim, and every rule, policy or agreement provision cited, with its words where quoted.',
    '4. The evidence: witnesses and what each said in substance; exhibits; what was asked for and refused or produced.',
    '5. Each side\'s arguments and objections, and how each was answered.',
    '6. Any time limit stated or invoked, and whether the file shows it met.',
    'If a page is unreadable or missing, say so.',
  ].join('\n');
}

// What the model is told for the assessment, given every section's extract. Bob's words are the brief.
export const ASSESSMENT_INSTRUCTION = [
  'You are assessing an old railroad labor case file for a General Chairperson of the union, from the extracts below (each',
  'cites the page of the whole file). His instruction: "a thorough critical assessment, but balanced fairly and even',
  'handedly - just honest but real too."',
  '',
  'Rules for the assessment:',
  '- Be critical of BOTH sides where the file shows a fault: the union\'s representatives as much as the carrier.',
  '- Be fair: give credit where the file shows something done well, by either side.',
  '- Be honest and plain. No flattery, no hedging for its own sake, no advocacy dressed as analysis.',
  '- Invent nothing. Every finding cites the page(s) it rests on. Where the file does not show something, say "the file',
  '  does not show" rather than guessing. Do not cite any award, case or rule that is not in the file.',
  '- Do not decide what the agreement means as the final word: interpreting the agreement is the General Chairperson\'s',
  '  authority. Say what the file shows each side relied on and how strong it looks.',
  '',
  'Write these sections, with these headings:',
  '1. THE CASE IN BRIEF - what happened, the charge or claim, the outcome.',
  '2. TIMELINE AND ANALYTICS - every dated act in order with the days between them; each time limit invoked or that',
  '   applies on the face of the file, and whether each side met it.',
  '3. THE CARRIER\'S HANDLING - procedure, evidence, fairness of the hearing, the decision; strengths and faults.',
  '4. THE UNION\'S HANDLING - representation, objections, requests, preparation, appeals; strengths and faults.',
  '5. THE DECISION AND THE APPEALS - was the result supported by the record, and were the appeals well made.',
  '6. WHAT SHOULD HAVE BEEN DONE DIFFERENTLY - by each side, concretely.',
  '7. LESSONS FOR FUTURE CASES - practical, for the committee\'s officers.',
  '8. WHAT THE FILE DOES NOT SHOW - gaps that limit this assessment.',
].join('\n');

// Across the committee's assessed files.
export const PATTERNS_INSTRUCTION = [
  'Below are assessments of several old case files of one General Committee. Identify the patterns across them, with the',
  'same standard: thorough, critical, balanced between union and carrier, honest and real, nothing invented, each point',
  'naming the files it rests on. Headings: RECURRING CARRIER PRACTICES; RECURRING UNION STRENGTHS; RECURRING UNION',
  'WEAKNESSES; TIME LIMITS (met and missed, by whom); WHAT WINS AND WHAT LOSES; RECOMMENDATIONS FOR THE COMMITTEE.',
].join('\n');

// The line under the box, so nobody mistakes an assessment for the committee's position.
export const BOX_NOTE = 'An assessment is an aid to the General Chairperson\'s own judgment, read from the file alone. It is not '
  + 'the committee\'s position and decides nothing; interpreting the agreement is the General Chairperson\'s.';
